/**
 * Tiny fuzzy-match helper for the dashboard global search.
 *
 * Scoring (lower = better, threshold at top of fuzzyScore):
 *   0       exact (case-insensitive) match
 *   ~1-2    substring match (keep current behaviour priority)
 *   3-N     Levenshtein distance fallback against label words + keywords
 *
 * Designed to be cheap: SEARCH_ITEMS is small (<50), each item has
 * a short label + keyword string, so a per-token Levenshtein scan
 * runs in microseconds — no need for a real fuzzy lib.
 */

// Iterative Levenshtein, O(m*n) with a single-row buffer.
const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j += 1) prev[j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    let curr = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      const next = Math.min(
        prev[j] + 1,        // deletion
        curr + 1,           // insertion
        prev[j - 1] + cost, // substitution
      );
      prev[j - 1] = curr;
      curr = next;
    }
    prev[b.length] = curr;
  }
  return prev[b.length];
};

// Per-token fuzzy distance: best (lowest) edit distance between the
// query and any whitespace-separated token in the haystack. Lets
// "challange" find "challenges" inside "challenge log action".
const bestTokenDistance = (query, haystack) => {
  const tokens = haystack.split(/\s+/).filter(Boolean);
  let best = Infinity;
  for (const tok of tokens) {
    // Compare against the prefix of the token of the same length as
    // the query — keeps long tokens from skewing the score.
    const slice = tok.slice(0, Math.max(query.length, tok.length));
    const d = levenshtein(query, slice);
    if (d < best) best = d;
    if (best === 0) return 0;
  }
  return best;
};

/**
 * Returns a score for `item` against the search query.
 * Lower is better. Items above `maxDistance` should be filtered out.
 */
export const fuzzyScore = (query, item) => {
  const q = query.toLowerCase().trim();
  if (!q) return Infinity;
  const label = item.label.toLowerCase();
  const keywords = (item.keywords || '').toLowerCase();
  const haystack = `${label} ${keywords}`;

  // 1. Exact label match
  if (label === q) return 0;
  // 2. Substring hits — keep existing strict-match behaviour as
  //    the highest priority result.
  if (label.includes(q)) return 1;
  if (keywords.includes(q)) return 2;

  // 3. Fuzzy distance.
  //    Multi-word queries (e.g. "skil tree"): sum the best per-token
  //    distance against the haystack so "skil tree" still resolves to
  //    "Skill Tree" (skil→skill = 1, tree→tree = 0, sum = 1).
  const queryTokens = q.split(/\s+/).filter(Boolean);
  if (queryTokens.length > 1) {
    let total = 0;
    for (const qt of queryTokens) {
      total += bestTokenDistance(qt, haystack);
    }
    return 3 + total;
  }
  // Single-word fuzzy fallback.
  const labelDist = bestTokenDistance(q, label);
  const kwDist = bestTokenDistance(q, keywords);
  return 3 + Math.min(labelDist, kwDist);
};

/**
 * Filters + sorts SEARCH_ITEMS for a given query.
 * `maxDistance` controls how forgiving the fuzzy match is —
 * 2 catches single-character typos (challange → challenges),
 * 3 catches two-char typos. We default to 2 to avoid noisy hits.
 */
export const fuzzyFilter = (items, query, maxDistance = 2) => {
  const q = query.trim();
  if (!q) return [];
  const scored = items
    .map((item) => ({ item, score: fuzzyScore(q, item) }))
    .filter(({ score }) => score <= 3 + maxDistance)
    .sort((a, b) => a.score - b.score);
  return scored.map(({ item }) => item);
};
